import { z } from 'zod'
import { Timestamp } from './common.js'

// Revision + AuditEntry + ModerationFlag entities.

export const RevisionDoc = z.object({
  id: z.string().uuid(),
  pk: z.string(), // = route_id lookup key
  route_id: z.string().uuid(),
  rev_index: z.number().int().positive(),
  editor_id: z.string().uuid(),
  parent_etag: z.string(),
  resulting_etag: z.string(),
  blob_path: z.string(), // canonical-v1/routes/<id>/revisions/<iso>-<rev_id>.json
  blob_url: z.string().url(),
  message: z.string().max(500).optional(),
  kind: z.enum(['edit', 'status']).optional(),
  conflicts: z.array(z.string()).default([]),
  created_at: Timestamp,
})
export type RevisionDoc = z.infer<typeof RevisionDoc>

export const RevisionPublic = RevisionDoc.omit({ pk: true, kind: true })
export type RevisionPublic = z.infer<typeof RevisionPublic>

export const AuditAction = z.enum([
  'route.create',
  'route.update',
  'route.delete',
  'route.publish',
  'route.revert',
  'route.request-publish',
  'route.cancel-publish',
  'route.approve-publish',
  'route.reject-publish',
  'route.archive',
  'route.restore',
  'route.reassign',
  'proposal.create',
  'proposal.approve',
  'proposal.reject',
  'media.create',
  'media.delete',
  'track.create',
  'track.delete',
  'avatar.update',
  'avatar.delete',
  'account.delete',
  'moderation.flag',
  'moderation.resolve',
])
export type AuditAction = z.infer<typeof AuditAction>

export const AuditEntryDoc = z.object({
  id: z.string().uuid(),
  pk: z.string(), // = bucket (date YYYY-MM-DD) for day-level audit queries
  bucket: z.string(),
  action: AuditAction,
  actor_id: z.string().uuid(),
  target_id: z.string(),
  target_kind: z.enum(['route', 'media', 'track', 'proposal', 'user']),
  metadata: z.record(z.unknown()).optional(),
  created_at: Timestamp,
})
export type AuditEntryDoc = z.infer<typeof AuditEntryDoc>

export const ModerationReason = z.enum(['spam', 'misinformation', 'unsafe', 'copyright', 'other'])
export type ModerationReason = z.infer<typeof ModerationReason>

export const ModerationStatus = z.enum(['open', 'resolved', 'dismissed'])
export type ModerationStatus = z.infer<typeof ModerationStatus>

export const ModerationFlagDoc = z.object({
  id: z.string().uuid(),
  pk: z.string(), // = target_id lookup key
  target_id: z.string(),
  target_kind: z.enum(['route', 'media', 'track']),
  reason: ModerationReason,
  note: z.string().max(1000).optional(),
  reporter_id: z.string().uuid(),
  status: ModerationStatus,
  resolver_id: z.string().uuid().optional(),
  resolution_note: z.string().max(1000).optional(),
  resolved_at: Timestamp.optional(),
  created_at: Timestamp,
})
export type ModerationFlagDoc = z.infer<typeof ModerationFlagDoc>

export const ModerationFlagPublic = ModerationFlagDoc.omit({ pk: true })
export type ModerationFlagPublic = z.infer<typeof ModerationFlagPublic>

export const ModerationCreateRequest = z.object({
  target_id: z.string().min(1),
  target_kind: z.enum(['route', 'media', 'track']),
  reason: ModerationReason,
  note: z.string().max(1000).optional(),
})
export type ModerationCreateRequest = z.infer<typeof ModerationCreateRequest>

export const ModerationResolveRequest = z.object({
  status: z.enum(['resolved', 'dismissed']),
  resolution_note: z.string().max(1000).optional(),
})
export type ModerationResolveRequest = z.infer<typeof ModerationResolveRequest>

export function revisionToPublic(doc: RevisionDoc): RevisionPublic {
  const { pk: _pk, kind: _kind, ...rest } = doc
  return rest
}

export function moderationToPublic(doc: ModerationFlagDoc): ModerationFlagPublic {
  const { pk: _pk, ...rest } = doc
  return rest
}

export function todayBucket(date = new Date()): string {
  return date.toISOString().slice(0, 10)
}
